import {
  add,
  format,
  getHours,
  getMinutes,
  getSeconds,
  Interval,
  isAfter,
  isBefore,
  isSameDay,
  isSameHour,
  isSameMinute,
  isSameSecond,
  set,
  startOfDay,
  sub,
  toDate,
} from 'date-fns';
import * as R from 'ramda';
import { WeekNumber as BaseWeekNumber, DayPickerProps, Matcher, useDayPicker, WeekNumberProps } from 'react-day-picker';
import { AnyStateMachine, assign, createMachine, MachineConfig } from 'xstate';
import { FloorDateOptions, FormatterProps, MachineContext, MachineEvent, StateTarget } from './models';

export type FormatRangeFunction = (range: Interval) => string;

const timeChanged =
  (prop: 'start' | 'end') =>
  (context: MachineContext, event: MachineEvent & { hours: number; minutes: number; seconds: number }): boolean => {
    return !R.allPass([
      (date: Date | number) => getHours(date) === event.hours,
      (date: Date | number) => getMinutes(date) === event.minutes,
      (date: Date | number) => getSeconds(date) === event.seconds,
    ])(new Date(context.range[prop]));
  };

export const isSameTime = R.allPass([isSameHour, isSameMinute, isSameSecond]);
export const isStartOfDay = (date: number | Date): boolean =>
  getHours(date) === 0 && getMinutes(date) === 0 && getSeconds(date) === 0;

const transitions = {
  changeTimes: {
    'change:start-time': {
      actions: ['setStartTime'],
      cond: timeChanged('start'),
    },
    'change:end-time': {
      actions: ['setEndTime'],
      cond: timeChanged('end'),
    },
  },
};

export const selectDayRange = (context: MachineContext, event: MachineEvent): MachineContext => {
  const { range: previous } = context;

  const range = {
    start: set(event.date as Date, {
      hours: getHours(previous.start),
      minutes: getMinutes(previous.start),
      seconds: getSeconds(previous.start),
    }),
    end: set(event.date as Date, {
      hours: getHours(previous.end),
      minutes: getMinutes(previous.end),
      seconds: getSeconds(previous.end),
    }),
  };

  if (isStartOfDay(range.end)) {
    range.end = add(range.end, { days: 1 });
  }

  const limit = context.rangeMaxDays
    ? {
        start: add(range.start, { days: -context.rangeMaxDays + 1 }),
        end: add(range.start, { days: context.rangeMaxDays - 1 }),
      }
    : context.limit;

  return {
    ...context,
    range,
    limit,
  };
};

const notSameDay = (context: MachineContext, event: MachineEvent & { date: number | Date }): boolean =>
  !isSameDay(context.range.start, event.date);

export const initialStateForRange = R.cond([
  [(range: Interval<Date>) => isSameDay(range.start, range.end), R.always(StateTarget.OneDateSelected)],
  [(range) => !!range.start && !!range.end, R.always(StateTarget.TwoDatesSelected)],
  [(range) => !!range.start || !!range.end, R.always(StateTarget.OneDateSelected)],
  [R.T, R.always(StateTarget.NoDatesSelected)],
]);

export const formatRange = ({
  patterns: formatPatterns,
  dateOnly = false,
}: Readonly<FormatterProps>): FormatRangeFunction =>
  R.cond([
    // Same day and time? Display as
    // * `02.06.2022, 08:00` or
    // * `02.06.2022`
    [
      (range: Interval) => isSameDay(range.start, range.end) && isSameTime(range.start, range.end),
      (range: Interval) =>
        [format(range.start, formatPatterns.date), !dateOnly && format(range.start, formatPatterns.time)]
          .filter(Boolean)
          .join(', '),
    ],

    // Same day, different time? Display as
    // * `02.06.2022, 08:00 bis 09:00` or
    // * `02.06.2022`
    [
      (range: Interval) => isSameDay(range.start, range.end),
      (range: Interval) =>
        [
          format(range.start, formatPatterns.date),
          !dateOnly &&
            [format(range.start, formatPatterns.time), format(range.end, formatPatterns.time)].join(
              formatPatterns.connector,
            ),
        ]
          .filter(Boolean)
          .join(', '),
    ],

    // Different days, same or different time? Display as
    // * `02.06.2022, 08:00 bis 05.06.2022, 08:00` or
    // * `02.06.2022 bis 05.06.2022`
    [
      R.T,
      (range: Interval) =>
        [
          [format(range.start, formatPatterns.date), !dateOnly && format(range.start, formatPatterns.time)]
            .filter(Boolean)
            .join(', '),

          [format(range.end, formatPatterns.date), !dateOnly && format(range.end, formatPatterns.time)]
            .filter(Boolean)
            .join(', '),
        ].join(formatPatterns.connector),
    ],
  ]);

export const convertToMatcherArray = (disabled: DayPickerProps['disabled']): Matcher[] => {
  let disabledArray: Matcher[] = [];

  if (disabled && typeof disabled === 'object' && ('before' in disabled || 'after' in disabled)) {
    disabledArray = [disabled];
  }

  if (disabled && typeof disabled === 'object' && 'length' in disabled) {
    disabledArray = disabled;
  }

  return disabledArray;
};

export const createWeekNumberComponent = (disabled: Matcher[]) =>
  function WeekNumberComponent(props: WeekNumberProps): React.JSX.Element {
    const { number, dates } = props;
    const {
      styles,
      classNames,
      locale,
      formatters: { formatWeekNumber },
    } = useDayPicker();

    function checkRule(rule: Matcher): boolean {
      let rulesApply = false;
      if (rule && typeof rule === 'object') {
        if ('before' in rule) {
          rulesApply = dates.every((date) => isBefore(date, rule.before)) || rulesApply;
        }
        if ('after' in rule) {
          rulesApply = dates.every((date) => isAfter(date, rule.after)) || rulesApply;
        }
      }

      return rulesApply;
    }

    const content = formatWeekNumber(Number(number), { locale });

    return disabled.some(checkRule) ? (
      <span aria-disabled={true} className={classNames.weeknumber} style={styles.weeknumber}>
        {content}
      </span>
    ) : (
      <BaseWeekNumber {...props} />
    );
  };

// Round the date to the floor of the nearest hour, minutes and seconds
export const floorDate = (options: FloorDateOptions) => (date: number | Date) => {
  const { hoursStep = 1, minutesStep, secondsStep } = options;

  // Milliseconds are always irrelevant, so they cannot be allowed to interfere with date comparisons
  const flooredDate = toDate(date);
  flooredDate.setMilliseconds(0);

  return sub(flooredDate, {
    hours: getHours(flooredDate) % hoursStep,
    minutes: getMinutes(flooredDate) % (minutesStep ?? hoursStep * 60),
    seconds: getSeconds(flooredDate) % (secondsStep ?? hoursStep * 60 * 60),
  });
};

export const steps = {
  hours: 1,
  minutes: 60,
  seconds: 3600,
};

export const createDateMachine = (date: Date): AnyStateMachine => {
  const config: MachineConfig<MachineContext, never, MachineEvent> = {
    schema: {
      context: {} as MachineContext,
      events: {} as { type: 'click:day'; date: Date },
    },
    id: 'select-date',
    initial: StateTarget.OneDateSelected,
    context: {
      range: {
        start: date,
        end: date,
      },
      limit: {},
    },
    states: {
      [StateTarget.NoDatesSelected]: {
        on: {
          'click:day': {
            target: StateTarget.OneDateSelected,
            actions: ['selectDay'],
          },
          ...transitions.changeTimes,
        },
      },
      [StateTarget.OneDateSelected]: {
        on: {
          'click:day': {
            target: StateTarget.OneDateSelected,
            actions: ['selectDay'],
          },
          ...transitions.changeTimes,
        },
      },
    },
  };

  return createMachine(config, {
    actions: {
      selectDay: assign(selectDayRange),
      setStartTime: assign({
        range: (context, { hours, minutes, seconds = 0 }) => ({
          ...context.range,
          start: set(context.range.start, { hours, minutes, seconds }),
        }),
      }),
    },
  });
};

export const createDateRangeMachine = (range: Interval, rangeMaxDays?: number): AnyStateMachine => {
  const config: MachineConfig<MachineContext, never, MachineEvent> = {
    id: 'select-date-range',
    initial: initialStateForRange(range),
    context: {
      range,
      rangeMaxDays,
      limit: {},
    },
    states: {
      [StateTarget.NoDatesSelected]: {
        on: {
          'click:day': {
            target: StateTarget.OneDateSelected,
            actions: ['selectDay'],
          },
          'click:week-number': {
            target: StateTarget.WeekSelected,
            actions: ['selectRange'],
          },
          // FIXME: No date.. what now?
          // ...transitions.changeTimes,
        },
      },
      [StateTarget.OneDateSelected]: {
        on: {
          'click:day': {
            target: StateTarget.TwoDatesSelected,
            actions: ['selectRange'],
            cond: notSameDay,
          },
          'click:week-number': {
            target: StateTarget.WeekSelected,
            actions: ['selectRange'],
          },
          ...transitions.changeTimes,
        },
      },
      [StateTarget.TwoDatesSelected]: {
        on: {
          'click:day': {
            target: StateTarget.OneDateSelected,
            actions: ['selectDay'],
          },
          'click:week-number': {
            target: StateTarget.WeekSelected,
            actions: ['selectRange'],
          },
          ...transitions.changeTimes,
        },
      },
      [StateTarget.WeekSelected]: {
        on: {
          'click:day': {
            target: StateTarget.OneDateSelected,
            actions: ['selectDay'],
          },
          'click:week-number': {
            target: StateTarget.WeekSelected,
            actions: ['selectRange'],
          },
          ...transitions.changeTimes,
        },
      },
    },
  };

  return createMachine(config, {
    actions: {
      selectDay: assign(selectDayRange),
      selectRange: assign({
        range: (context, event) => {
          const { range: previous } = context;
          const dates = event.range ? [event.range.start, event.range.end] : [previous.start, event.date];

          const [start, end] = R.sortBy(R.identity, dates as Date[]);

          // This may look redundant but don't forget that the dates may have
          // been swapped but the time must stay the same.

          const r = {
            start: set(start, {
              hours: getHours(previous.start),
              minutes: getMinutes(previous.start),
              seconds: getSeconds(previous.start),
            }),
            end: set(end, {
              hours: getHours(previous.end),
              minutes: getMinutes(previous.end),
              seconds: getSeconds(previous.end),
            }),
          };

          if (isStartOfDay(r.end)) {
            r.end = add(r.end, { days: 1 });
          }

          // FIXME this ignores `rangeMaxDays`, will cause wrong behaviour max selectable days is less than a week

          return r;
        },
      }),
      setStartTime: assign({
        range: (context, { hours, minutes, seconds = 0 }) => ({
          ...context.range,
          start: set(context.range.start, { hours, minutes, seconds }),
        }),
      }),
      setEndTime: assign({
        range: (context, { hours, minutes, seconds }, meta) => {
          if (hours === 0 && minutes === 0 && seconds === 0) {
            if (meta?.state?.value === StateTarget.OneDateSelected) {
              return {
                ...context.range,
                end: add(startOfDay(context.range.start), { days: 1 }),
              };
            }

            if (meta?.state?.value === StateTarget.TwoDatesSelected) {
              return {
                ...context.range,
                end: add(startOfDay(context.range.end), { days: 1 }),
              };
            }
          }

          if (meta?.state?.value === StateTarget.TwoDatesSelected && isStartOfDay(Number(context.range.end))) {
            return {
              ...context.range,
              end: add(set(context.range.end, { hours, minutes, seconds }), { days: -1 }),
            };
          }

          return {
            ...context.range,
            end: set(meta?.state?.value === StateTarget.OneDateSelected ? context.range.start : context.range.end, {
              hours,
              minutes,
              seconds,
            }),
          };
        },
      }),
    },
  });
};
